import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Table, Spinner, Button, ButtonGroup, OverlayTrigger, Tooltip as BsTooltip } from 'react-bootstrap';
import { DollarSign, ShoppingCart, TrendingUp, TrendingDown, Wallet, CalendarDays, Tag, Receipt, Settings } from 'lucide-react';
import FixedCostsModal from './FixedCostsModal';
import ExpenseVouchersModal from './ExpenseVouchersModal';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import KPICard from '../cards/KPICard';
import './RevenueSection.css';

const GROUP_BY_LABELS = {
  DAY: { tab: 'Ngày', noun: 'ngày' },
  WEEK: { tab: 'Tuần', noun: 'tuần' },
  MONTH: { tab: 'Tháng', noun: 'tháng' },
};

const formatBucketLabel = (isoDate, groupBy) => {
  // isoDate là YYYY-MM-DD theo lịch VN (bucket start). Parse như local để tránh
  // bị toLocaleDateString convert sang UTC làm lệch ngày.
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (groupBy === 'MONTH') {
    return dt.toLocaleDateString('vi-VN', { month: '2-digit', year: 'numeric' });
  }
  if (groupBy === 'WEEK') {
    return `Tuần ${dt.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}`;
  }
  return dt.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
};

/**
 * #071245 — nhãn của một dòng bảng "Doanh thu theo tài khoản ngân hàng".
 *
 * Hai dòng KHÔNG có tài khoản nhận (`CASH` và `UNKNOWN_BANK`) trước đây hiện y
 * hệt nhau ("Tiền mặt" / "-" / "-"), người xem không biết dòng nào là dòng nào.
 * Nay tách bằng `bucket_key` do máy chủ trả về.
 */
export const bankBucketLabel = (item) => {
  if (item?.bucket_key === 'CASH') return 'Tiền mặt';
  if (item?.bucket_key === 'UNKNOWN_BANK') return 'Chưa gắn tài khoản nhận';
  return item?.short_name || item?.bank_name || 'Tài khoản khác';
};

/**
 * #174558 — số ngày (lịch) của kỳ báo cáo, đếm BAO GỒM cả ngày đầu và ngày cuối.
 *
 * Lấy từ `period` do MÁY CHỦ trả về (chính khoảng đã dùng để tính báo cáo), không lấy
 * từ state của trang để tránh lệch kỳ.
 */
export const countPeriodDays = (period) => {
  const from = period?.from_date;
  const to = period?.to_date;
  if (!from || !to) return 0;
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return 0;
  return Math.max(1, Math.round((toMs - fromMs) / 86400000) + 1);
};

const RevenueSection = ({ data, loading, selectedBranch, onReload, groupBy = 'DAY', onGroupByChange }) => {
  const navigate = useNavigate();
  const [showFixedCostsModal, setShowFixedCostsModal] = useState(false);
  // #235631: bấm vào con số chi phí NVL để xem danh sách phiếu chi cấu thành nên nó.
  const [showMaterialVouchers, setShowMaterialVouchers] = useState(false);
  // #004730: dòng "Khác" cũng bấm xem được phiếu chi cấu thành, y như "NVL".
  const [showOtherVouchers, setShowOtherVouchers] = useState(false);
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Đang tải dữ liệu doanh thu...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-5">
        <p>Không có dữ liệu</p>
      </div>
    );
  }

  const { summary, by_order_type, by_account_bank, daily_revenue, top_selling_items } = data;

  /**
   * #071245 — bấm 1 dòng bảng theo tài khoản ngân hàng → mở màn "Quản lý đơn hàng"
   * đã set sẵn bộ lọc ra ĐÚNG các đơn cộng nên con số của dòng đó.
   *
   * Khoảng thời gian lấy từ `data.period` — tức chính khoảng MÁY CHỦ đã dùng để
   * tính báo cáo này, không phải ngày hôm nay hay tháng này. Nếu lấy từ chỗ khác
   * (state của trang, mặc định của màn đơn hàng) thì kỳ có thể lệch và số sẽ sai.
   */
  const openBankBucketOrders = (item) => {
    const bucket = item?.bucket_key || item?.bank_account_id;
    if (!bucket) return;
    const params = new URLSearchParams();
    params.set('bank_bucket', bucket);
    if (data?.period?.from_date) params.set('from', data.period.from_date);
    if (data?.period?.to_date) params.set('to', data.period.to_date);
    // Chi nhánh đi theo BranchContext (param `branch_id` dùng chung toàn app),
    // giữ nguyên chi nhánh đang xem báo cáo.
    if (selectedBranch?.id) params.set('branch_id', selectedBranch.id);
    navigate(`/orders?${params.toString()}`);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}k`;
    }
    return amount.toLocaleString('vi-VN');
  };

  // Transform data for charts
  const dailyRevenueData = daily_revenue?.map((item) => ({
    date: formatBucketLabel(item.date, groupBy),
    revenue: item.total_amount,
    orders: item.order_count,
  })) || [];

  // Growth indicator (mũi tên + %) so với kỳ liền trước
  const growthPct = summary?.growth_percentage;
  const hasGrowth = growthPct !== null && growthPct !== undefined;
  const revenueTrend = hasGrowth
    ? { value: growthPct, isPositive: growthPct >= 0 }
    : undefined;
  const previousPeriod = summary?.previous_period;

  const orderTypeData = by_order_type?.map((item) => ({
    name: item.order_type === 'DINE_IN' ? 'Tại quán' : item.order_type === 'TAKEAWAY' ? 'Mang đi' : item.order_type === 'DELIVERY' ? 'Giao hàng' : item.order_type,
    value: item.total_amount,
    count: item.order_count,
  })) || [];

  /**
   * #233523 — góp ý qua nút Trợ năng: thẻ "Tổng chi phí" hiện thêm 2 tỉ lệ
   * "% lương+thưởng / doanh thu" và "% chi phí cố định / doanh thu".
   *
   * Tính ngay tại đây từ các số MÁY CHỦ đã trả sẵn trong `summary` (lương, thưởng,
   * chi phí cố định, doanh thu) — cùng mẫu số `total_revenue` với 2 tỉ lệ đang hiện
   * (`material_cost_ratio`, `cost_ratio`) nên 4 dòng luôn cùng một kỳ, không lệch nhau.
   * Doanh thu bằng 0 thì để 0 (không chia), giống cách máy chủ đang làm.
   */
  const totalRevenueForRatio = summary?.total_revenue || 0;
  const laborCost = (summary?.employee_wage_cost || 0) + (summary?.employee_bonus_cost || 0);
  const laborCostRatio = totalRevenueForRatio > 0 ? (laborCost / totalRevenueForRatio) * 100 : 0;
  const fixedCostRatio =
    totalRevenueForRatio > 0 ? ((summary?.fixed_costs_total || 0) / totalRevenueForRatio) * 100 : 0;

  // Calculate cash percentage
  const cashPercentage = summary?.total_revenue > 0
    ? ((summary.cash_revenue / summary.total_revenue) * 100).toFixed(1) 
    : 0;

  // #174558 — doanh thu trung bình mỗi ngày trong kỳ = tổng doanh thu ÷ số ngày của kỳ.
  const periodDays = countPeriodDays(data?.period);
  const avgRevenuePerDay = periodDays > 0 ? (summary?.total_revenue || 0) / periodDays : 0;

  // #174558 — "Chờ thanh toán" dời từ thẻ KPI gọn lên NGAY BÊN CẠNH con số "Tổng doanh thu".
  const pendingPaymentAside =
    `Chờ thanh toán (${summary?.pending_payment_orders ?? 0} đơn): ` +
    `${formatCurrency(summary?.pending_payment_amount || 0)}đ`;

  return (
    <div className="revenue-section">


      {/* #223257 — gộp 4 thẻ KPI phụ vào MỘT thẻ gọn: lưới 2 cột trên điện thoại, 4 cột trên
          máy tính. Thẻ "Tổng doanh thu" vẫn đứng riêng làm số chính (có mũi tên so kỳ trước).
          #174558 — "Chờ thanh toán" dời lên NGAY BÊN CẠNH con số "Tổng doanh thu"; chỗ trống
          trong thẻ gọn nhận "Doanh thu trung bình ngày" (thẻ gọn vẫn đủ 4 ô). */}
      <Row className="mb-4">
        <Col xs={12} lg={3}>
          {previousPeriod ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <BsTooltip>
                  Kỳ trước ({previousPeriod.from_date} → {previousPeriod.to_date}):{' '}
                  {(summary?.previous_total_revenue || 0).toLocaleString('vi-VN')}đ
                </BsTooltip>
              }
            >
              <div>
                <KPICard
                  title={`Tổng doanh thu (${summary?.total_orders ?? 0} đơn)`}
                  value={formatCurrency(summary?.total_revenue || 0)}
                  icon={DollarSign}
                  color="primary"
                  suffix="đ"
                  trend={revenueTrend}
                  valueAside={pendingPaymentAside}
                />
              </div>
            </OverlayTrigger>
          ) : (
            <KPICard
              title={`Tổng doanh thu (${summary?.total_orders ?? 0} đơn)`}
              value={formatCurrency(summary?.total_revenue || 0)}
              icon={DollarSign}
              color="primary"
              suffix="đ"
              trend={revenueTrend}
              valueAside={pendingPaymentAside}
            />
          )}
        </Col>
        <Col xs={12} lg={9}>
          <Card className="kpi-compact-card h-100">
            <Card.Body>
              <div className="kpi-compact-grid">
                <div className="kpi-compact-tile">
                  <div className="kpi-compact-head">
                    <span className="kpi-compact-icon" style={{ backgroundColor: '#52c41a15' }}>
                      <CalendarDays size={18} style={{ color: '#52c41a' }} />
                    </span>
                    <span className="kpi-compact-label">Doanh thu trung bình ngày</span>
                  </div>
                  <div className="kpi-compact-value">
                    {formatCurrency(avgRevenuePerDay)}
                    <span className="kpi-compact-suffix">đ</span>
                  </div>
                </div>
                <div className="kpi-compact-tile">
                  <div className="kpi-compact-head">
                    <span className="kpi-compact-icon" style={{ backgroundColor: '#1890ff15' }}>
                      <TrendingUp size={18} style={{ color: '#1890ff' }} />
                    </span>
                    <span className="kpi-compact-label">Giá trị TB/đơn</span>
                  </div>
                  <div className="kpi-compact-value">
                    {formatCurrency(summary?.avg_order_value || 0)}
                    <span className="kpi-compact-suffix">đ</span>
                  </div>
                </div>
                <div className="kpi-compact-tile">
                  <div className="kpi-compact-head">
                    <span className="kpi-compact-icon" style={{ backgroundColor: '#faad1415' }}>
                      <Wallet size={18} style={{ color: '#faad14' }} />
                    </span>
                    <span className="kpi-compact-label">Doanh thu tiền mặt</span>
                  </div>
                  <div className="kpi-compact-value">{`${cashPercentage}%`}</div>
                </div>
                <div className="kpi-compact-tile">
                  <div className="kpi-compact-head">
                    <span className="kpi-compact-icon" style={{ backgroundColor: '#f5222d15' }}>
                      <Tag size={18} style={{ color: '#f5222d' }} />
                    </span>
                    <span className="kpi-compact-label">Tổng giảm giá</span>
                  </div>
                  <div className="kpi-compact-value">
                    {formatCurrency(summary?.total_discount_amount || 0)}
                    <span className="kpi-compact-suffix">đ</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* KPI Cards - Row 2: Chi phí (tách NVL/Khác/Cố định), Lợi nhuận.
          "Tổng giảm giá" đã gộp vào thẻ KPI gọn ở hàng trên (#223257). */}
      <Row className="mb-4">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
          <Card className="kpi-card h-100" style={{ borderLeft: '4px solid #6c757d' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="text-muted small d-flex align-items-center" style={{ gap: 6 }}>
                    <Receipt size={14} /> Tổng chi phí
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <BsTooltip>
                          NVL: {(summary?.material_cost || 0).toLocaleString('vi-VN')}đ
                          <br />
                          Chi phí khác: {(summary?.other_expenses || 0).toLocaleString('vi-VN')}đ
                          <br />
                          Chi phí cố định (chia tỷ lệ ngày): {(summary?.fixed_costs_total || 0).toLocaleString('vi-VN')}đ
                          <br />
                          Lương nhân viên (giờ ca hoàn thành × lương/giờ): {(summary?.employee_wage_cost || 0).toLocaleString('vi-VN')}đ
                          <br />
                          Thưởng nhân viên (điểm thưởng quy ra tiền): {(summary?.employee_bonus_cost || 0).toLocaleString('vi-VN')}đ
                          <br />
                          Phạt nhân viên (không trừ vào chi phí): {(summary?.employee_penalty_amount || 0).toLocaleString('vi-VN')}đ
                          <br />
                          Rút tiền mặt (không tính chi phí): {(summary?.cash_withdrawal_total || 0).toLocaleString('vi-VN')}đ
                        </BsTooltip>
                      }
                    >
                      <span style={{ cursor: 'help', color: '#888' }}>ⓘ</span>
                    </OverlayTrigger>
                  </div>
                  <div className="h4 mb-1 mt-1">
                    {formatCurrency(summary?.total_cost || 0)}<span className="text-muted small">đ</span>
                  </div>
                  <div className="small text-muted">
                    {/* #235631: "NVL" là con số đã chi cho nguyên vật liệu — bấm vào
                        mở danh sách phiếu chi cấu thành nên đúng con số này. */}
                    <Button
                      variant="link"
                      className="p-0 align-baseline small text-decoration-underline"
                      title="Xem các phiếu chi cấu thành con số NVL này"
                      onClick={(e) => { e.stopPropagation(); setShowMaterialVouchers(true); }}
                    >
                      NVL: {formatCurrency(summary?.material_cost || 0)}đ
                    </Button>
                    <span className="mx-1">·</span>
                    {/* #004730: góp ý admin qua nút Trợ năng — "Khác" cũng cho bấm xem
                        danh sách phiếu chi cấu thành giống NVL (nhóm OTHER). */}
                    <Button
                      variant="link"
                      className="p-0 align-baseline small text-decoration-underline"
                      title="Xem các phiếu chi cấu thành con số Chi phí khác này"
                      onClick={(e) => { e.stopPropagation(); setShowOtherVouchers(true); }}
                    >
                      Khác: {formatCurrency(summary?.other_expenses || 0)}đ
                    </Button>
                    <span className="mx-1">·</span>
                    Cố định: {formatCurrency(summary?.fixed_costs_total || 0)}đ
                    <span className="mx-1">·</span>
                    Lương: {formatCurrency(summary?.employee_wage_cost || 0)}đ
                    <span className="mx-1">·</span>
                    Thưởng: {formatCurrency(summary?.employee_bonus_cost || 0)}đ
                  </div>
                  {(summary?.cash_withdrawal_total || 0) > 0 && (
                    <div className="small text-muted" style={{ opacity: 0.7 }}>
                      Rút tiền mặt (không tính chi phí): {formatCurrency(summary.cash_withdrawal_total)}đ
                    </div>
                  )}
                  <div className="small mt-1">
                    <span className="text-muted">% NVL / doanh thu: </span>
                    <strong
                      className={
                        (summary?.material_cost_ratio || 0) > 40
                          ? 'text-danger'
                          : (summary?.material_cost_ratio || 0) > 30
                          ? 'text-warning'
                          : 'text-success'
                      }
                    >
                      {(summary?.material_cost_ratio || 0).toFixed(1)}%
                    </strong>
                  </div>
                  <div className="small text-muted">
                    % Lương + thưởng / doanh thu: {laborCostRatio.toFixed(1)}%
                  </div>
                  <div className="small text-muted">
                    % Chi phí cố định / doanh thu: {fixedCostRatio.toFixed(1)}%
                  </div>
                  <div className="small text-muted">
                    Tổng chi phí/doanh thu: {(summary?.cost_ratio || 0).toFixed(1)}%
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowFixedCostsModal(true)}
                  title="Cấu hình chi phí cố định"
                >
                  <Settings size={14} />
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card
            className="kpi-card h-100"
            style={{ borderLeft: `4px solid ${(summary?.gross_profit || 0) >= 0 ? '#52c41a' : '#f5222d'}` }}
          >
            <Card.Body>
              <div className="text-muted small d-flex align-items-center" style={{ gap: 6 }}>
                {(summary?.gross_profit || 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                Lợi nhuận
                <OverlayTrigger
                  placement="top"
                  overlay={<BsTooltip>Doanh thu − (NVL + Chi phí khác + Chi phí cố định + Lương nhân viên + Thưởng nhân viên)</BsTooltip>}
                >
                  <span style={{ cursor: 'help', color: '#888' }}>ⓘ</span>
                </OverlayTrigger>
              </div>
              <div
                className={`h4 mb-1 mt-1 ${(summary?.gross_profit || 0) >= 0 ? 'text-success' : 'text-danger'}`}
              >
                {formatCurrency(summary?.gross_profit || 0)}<span className="small">đ</span>
              </div>
              <div className="small text-muted">
                Biên lợi nhuận: {(summary?.profit_margin || 0).toFixed(1)}%
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ExpenseVouchersModal
        show={showMaterialVouchers}
        onHide={() => setShowMaterialVouchers(false)}
        title="Chi phí nguyên vật liệu (NVL) — các phiếu chi cấu thành"
        group="MATERIAL"
        branchId={selectedBranch?.id}
        branchName={selectedBranch?.name}
        fromDate={data?.period?.from_date}
        toDate={data?.period?.to_date}
        expectedTotal={summary?.material_cost}
      />

      <ExpenseVouchersModal
        show={showOtherVouchers}
        onHide={() => setShowOtherVouchers(false)}
        title="Chi phí khác — các phiếu chi cấu thành"
        group="OTHER"
        branchId={selectedBranch?.id}
        branchName={selectedBranch?.name}
        fromDate={data?.period?.from_date}
        toDate={data?.period?.to_date}
        expectedTotal={summary?.other_expenses}
      />

      <FixedCostsModal
        show={showFixedCostsModal}
        onHide={() => setShowFixedCostsModal(false)}
        selectedBranch={selectedBranch}
        onSaved={() => {
          setShowFixedCostsModal(false);
          if (typeof onReload === 'function') onReload();
        }}
      />

      {/* Charts Row */}
      <Row className="mb-4">
        {/* Daily Revenue Line Chart */}
        <Col xs={12}>
          <Card className="chart-card mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              {/*
                #000744: một "ngày" ở đây là NGÀY CỦA CA, không phải ngày lịch
                của lúc thanh toán. Ca 20:00–24:00 ngày N mà khách trả tiền lúc
                00:10 ngày N+1 vẫn nằm trọn ở ngày N. Nói rõ ngay trên biểu đồ,
                nếu không quản lý sẽ đối chiếu nhầm với sao kê ngân hàng (sao kê
                theo giờ tiền về, tức ngày lịch).
              */}
              <h5
                className="mb-0"
                title="Gom theo NGÀY CỦA CA: ca vắt qua nửa đêm tính trọn cho ngày ca bắt đầu, không bị xé làm hai ngày."
              >
                📈 Doanh thu theo {GROUP_BY_LABELS[groupBy]?.noun || 'ngày'} (theo ca)
              </h5>
              <ButtonGroup size="sm">
                {['DAY', 'WEEK', 'MONTH'].map((key) => (
                  <Button
                    key={key}
                    variant={groupBy === key ? 'primary' : 'outline-primary'}
                    onClick={() => onGroupByChange && onGroupByChange(key)}
                    disabled={!onGroupByChange}
                  >
                    {GROUP_BY_LABELS[key].tab}
                  </Button>
                ))}
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'revenue' ? `${value.toLocaleString('vi-VN')}đ` : value,
                      name === 'revenue' ? 'Doanh thu' : 'Số đơn',
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#1890ff"
                    strokeWidth={2}
                    fill="#1890ff"
                    fillOpacity={0.1}
                    name="Doanh thu"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Type Bar Chart - Commented out */}
      {/* <Row className="mb-4">
        <Col xs={12}>
          <Card className="chart-card mb-3">
            <Card.Header>
              <h5>📊 Doanh thu theo loại đơn</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={orderTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'value' ? `${value.toLocaleString('vi-VN')}đ` : value,
                      name === 'value' ? 'Doanh thu' : 'Số đơn',
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="value" fill="#1890ff" name="Doanh thu" />
                  <Bar dataKey="count" fill="#52c41a" name="Số đơn" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}

      {/* Bank Account Revenue Table */}
      {by_account_bank && by_account_bank.length > 0 && (
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="table-card mb-3">
              <Card.Header>
                <h5 className="mb-0">🏦 Doanh thu theo tài khoản ngân hàng</h5>
                <small className="text-muted">Bấm vào một dòng để xem danh sách đơn cấu thành con số đó</small>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Ngân hàng</th>
                        <th>Số tài khoản</th>
                        <th>Tên tài khoản</th>
                        <th className="text-end">Tổng doanh thu</th>
                        <th className="text-end">Số đơn</th>
                        <th className="text-end">Tỷ lệ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {by_account_bank.map((item, index) => (
                        <tr
                          key={item.bucket_key || item.bank_account_id || `bank-${index}`}
                          className="bank-revenue-row"
                          role="button"
                          tabIndex={0}
                          title={`Xem ${item.order_count} đơn cấu thành ${item.total_amount.toLocaleString('vi-VN')}đ`}
                          onClick={() => openBankBucketOrders(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openBankBucketOrders(item);
                            }
                          }}
                        >
                          <td>{index + 1}</td>
                          <td>
                            {bankBucketLabel(item)}
                            {item.bank_name && item.short_name && (
                              <small className="d-block text-muted">{item.bank_name}</small>
                            )}
                          </td>
                          <td>{item.account_no || '-'}</td>
                          <td>{item.account_name || '-'}</td>
                          <td className="text-end">{item.total_amount.toLocaleString('vi-VN')}đ</td>
                          <td className="text-end">{item.order_count}</td>
                          <td className="text-end">
                            <span className="badge bg-primary">{item.percentage.toFixed(2)}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Expense Breakdown Table */}
      {summary?.expense_breakdown && summary.expense_breakdown.length > 0 && (
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="table-card mb-3">
              <Card.Header>
                <h5>🧾 Cơ cấu chi phí</h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Nhóm</th>
                        <th>Khoản mục</th>
                        <th className="text-end">Số tiền</th>
                        <th className="text-end">% doanh thu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.expense_breakdown.map((item) => (
                        <tr key={item.category}>
                          <td>
                            {item.group === 'MATERIAL' ? (
                              <span className="badge bg-primary">NVL</span>
                            ) : item.group === 'OTHER' ? (
                              <span className="badge bg-secondary">Khác</span>
                            ) : (
                              <span className="badge bg-light text-dark">Không tính chi phí</span>
                            )}
                          </td>
                          <td>{item.label}</td>
                          <td className="text-end">{item.amount.toLocaleString('vi-VN')}đ</td>
                          <td className="text-end">{item.percentage_of_revenue.toFixed(1)}%</td>
                        </tr>
                      ))}
                      <tr className="fw-bold">
                        <td colSpan={2}>Tổng chi phí (NVL + Khác)</td>
                        <td className="text-end">{(summary?.total_expenses || 0).toLocaleString('vi-VN')}đ</td>
                        <td className="text-end">
                          {summary?.total_revenue > 0
                            ? (((summary?.total_expenses || 0) / summary.total_revenue) * 100).toFixed(1)
                            : '0.0'}
                          %
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Top Selling Items Table */}
      <Row>
        <Col xs={12}>
          <Card className="table-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>🏆 Top 10 món bán chạy</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Tên món</th>
                      <th className="text-end">Số lượng</th>
                      <th className="text-end">Doanh thu</th>
                      <th className="text-end">Số đơn</th>
                      <th className="text-end">Trung bình</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_selling_items?.length > 0 ? (
                      top_selling_items.map((item, index) => (
                        <tr key={item.menu_item_id}>
                          <td>{index + 1}</td>
                          <td>{item.item_name}</td>
                          <td className="text-end">{item.quantity_sold}</td>
                          <td className="text-end">{item.total_revenue.toLocaleString('vi-VN')}đ</td>
                          <td className="text-end">{item.order_count}</td>
                          <td className="text-end">
                            {(item.total_revenue / item.quantity_sold).toLocaleString('vi-VN')}đ
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          Không có dữ liệu
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RevenueSection;


